import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import type { AssignableRole, Role } from '@/lib/can-manage-role';
import type { Permission } from '@/lib/permissions';

export type MemberStatus = 'ACTIVE' | 'INVITED' | 'SUSPENDED';

/** Ligne de GET /users (tenants-service, listMembers) : membership + utilisateur à plat. */
export interface Member {
  membershipId: string;
  role: Role;
  /** Custom role assigné (le `role` reste le rang) */
  customRoleId?: string | null;
  joinedAt: string;
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  status: MemberStatus;
  phone: string | null;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface MembersPage { data: Member[]; page: number; pageSize: number; total: number; totalPages: number }

export interface MembersFilters {
  page: number;
  pageSize?: number;
  role?: AssignableRole;
  status?: MemberStatus;
  search?: string;
}

export const MEMBER_STATUS_LABELS: Record<MemberStatus, string> = {
  ACTIVE: 'Actif',
  INVITED: 'Invité',
  SUSPENDED: 'Suspendu',
};

export const MEMBER_STATUS_VARIANT: Record<MemberStatus, 'success' | 'secondary' | 'warning'> = {
  ACTIVE: 'success',
  INVITED: 'secondary',
  SUSPENDED: 'warning',
};

export const memberName = (m: Pick<Member, 'firstName' | 'lastName' | 'email'>): string =>
  [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email;

/** Ligne de GET /users?ids=… : de quoi afficher un nom, sans le membership. */
export type UserSummary = Pick<Member, 'id' | 'firstName' | 'lastName' | 'email'>;

/** Plafond serveur de GET /users?ids (aligné sur pageSize). */
const USERS_BY_IDS_MAX = 100;

/**
 * Noms des membres par userId (8.3.4) : un appel par lot de 100 ids, clé de cache sur les ids triés
 * (deux composants qui demandent les mêmes ids partagent la requête). Un id hors tenant est absent de la Map.
 */
export const useUsersByIds = (ids: readonly (string | null | undefined)[], enabled = true) => {
  const unique = [...new Set(ids.filter((id): id is string => !!id))].sort();
  return useQuery({
    queryKey: ['users-by-ids', unique],
    queryFn: async () => {
      const chunks: string[][] = [];
      for (let i = 0; i < unique.length; i += USERS_BY_IDS_MAX) chunks.push(unique.slice(i, i + USERS_BY_IDS_MAX));
      const pages = await Promise.all(
        chunks.map(async (c) => (await api.get<{ data: UserSummary[] }>(`/users?ids=${c.join(',')}`)).data.data),
      );
      return new Map(pages.flat().map((u) => [u.id, u]));
    },
    enabled: enabled && unique.length > 0,
    staleTime: 60_000,
  });
};

export const useMembers = (filters: MembersFilters, enabled = true) =>
  useQuery({
    queryKey: ['members', filters],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(filters.page), pageSize: String(filters.pageSize ?? 25) });
      if (filters.role) params.set('role', filters.role);
      if (filters.status) params.set('status', filters.status);
      if (filters.search?.trim()) params.set('search', filters.search.trim());
      return (await api.get<MembersPage>(`/users?${params}`)).data;
    },
    placeholderData: keepPreviousData,
    enabled,
  });

export const useInviteMember = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { email: string; role: AssignableRole; customRoleId?: string }) =>
      (await api.post<{ expiresAt: string }>('/users/invite', body)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['members'] });
      void qc.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
};

export const useChangeRole = () => {
  const qc = useQueryClient();
  return useMutation({
    // role : rang built-in ; customRoleId : remplace ses permissions (null = retour au built-in seul)
    mutationFn: async ({ membershipId, ...body }: { membershipId: string; role?: AssignableRole; customRoleId?: string | null }) =>
      (await api.patch<{ from?: Role; to?: Role; userId: string; customRoleId?: string | null }>(`/users/${membershipId}/role`, body)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['members'] });
      void qc.invalidateQueries({ queryKey: ['custom-roles'] });
    },
  });
};

export const useDeleteMember = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (membershipId: string) => { await api.delete(`/users/${membershipId}`); },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['members'] }),
  });
};

export type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED';

/** Ligne de GET /users/invitations (tenants-service) : le token ne circule que dans l'email. */
export interface Invitation {
  id: string;
  email: string;
  role: AssignableRole;
  /** null : rôle standard seul (ou custom role supprimé depuis l'invitation, `role` sert alors de repli). */
  customRoleId: string | null;
  status: InvitationStatus;
  invitedById: string | null;
  expiresAt: string;
  acceptedAt: string | null;
  createdAt: string;
}

export interface InvitationsPage { data: Invitation[]; total: number; page: number; pageSize: number }

export const useInvitations = (status: InvitationStatus = 'PENDING') =>
  useQuery({
    queryKey: ['invitations', status],
    queryFn: async () => (await api.get<InvitationsPage>(`/users/invitations?status=${status}&pageSize=100`)).data,
  });

export const useRevokeInvitation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/users/invitations/${id}`); },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['invitations'] }),
  });
};

export const useResendInvitation = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.post<{ expiresAt: string }>(`/users/invitations/${id}/resend`)).data,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['invitations'] }),
  });
};

/** Grant (granted) ou deny individuel par-dessus le rôle d'un membre (GET /users/:membershipId/overrides). */
export interface PermissionOverride {
  id: string;
  membershipId: string;
  permission: Permission;
  granted: boolean;
  createdById: string | null;
  createdAt: string;
}

export const useOverrides = (membershipId: string | undefined) =>
  useQuery({
    queryKey: ['overrides', membershipId],
    queryFn: async () => (await api.get<{ data: PermissionOverride[] }>(`/users/${membershipId}/overrides`)).data,
    enabled: !!membershipId,
  });

/** `granted: null` = retour à « hérité ». */
export interface OverrideChange { permission: Permission; granted: boolean | null }

// Tout-ou-rien côté serveur (transaction) : un refus n'applique aucun changement
export const useBatchOverrides = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ membershipId, changes }: { membershipId: string; changes: OverrideChange[] }) =>
      (await api.post<{ data: PermissionOverride[] }>(`/users/${membershipId}/overrides/batch`, { changes })).data,
    onSuccess: (_, v) => void qc.invalidateQueries({ queryKey: ['overrides', v.membershipId] }),
  });
};
